// backgroundPatterns/TransparentSwatch.jsx
//
// SHARED, reusable swatch for the app's explicit "Transparent" color choice
// (PATTERN_BG_ENABLED — the pattern-background LAW). No such swatch existed
// anywhere in the app before; this is the single canonical control for it, so
// every surface that offers an explicit-transparent option renders the SAME
// affordance. Any consumer can import it from the backgroundPatterns barrel.
//
// Visual: a red circle-with-slash (the universal "none"/prohibition mark) over a
// light checkerboard field. The checkerboard is the app's existing "shows-through"
// convention (the gradient builder's transparent-stop track, GradientBuilder.styled
// `TrackBar`); the red circle-slash is Gordon's verbatim spec for the explicit
// transparent choice — a DELIBERATE transparency, distinct from a silent omission.
//
// Accessibility: a real keyboard-operable button (role="button", tabIndex, Enter/
// Space), aria-label "Transparent", aria-pressed reflecting selection. The red mark
// is a shape (not color alone), and the accessible name states the meaning, so the
// choice never relies on color perception. 44px min tap target on coarse pointers
// is inherited from the host layout; the mark scales with `size`.

import React from "react";
import { Box, Tooltip } from "@mui/material";

const SELECTED_RING = "#9B5AF8"; // brand accent — matches the pattern grid selection ring
const SLASH_RED = "#E5484D"; // clear red for the circle-with-slash "none" mark

// Classic 8px light checkerboard so the "transparent" field reads as see-through.
const CHECKER = {
  backgroundColor: "#ffffff",
  backgroundImage:
    "linear-gradient(45deg, #d7dbe0 25%, transparent 25%)," +
    "linear-gradient(-45deg, #d7dbe0 25%, transparent 25%)," +
    "linear-gradient(45deg, transparent 75%, #d7dbe0 75%)," +
    "linear-gradient(-45deg, transparent 75%, #d7dbe0 75%)",
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
};

const TransparentSwatch = ({
  selected = false,
  onSelect = () => {},
  size = 34, // container px — matches ColorSwatch (isSegmentColor) so it sits inline
  label = "Transparent",
}) => {
  const inner = Math.round(size * 0.88);
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <Tooltip title={label} placement="top" arrow>
      <Box
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-pressed={selected}
        onClick={onSelect}
        onKeyDown={handleKey}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: "50%",
          border: selected ? `2px solid ${SELECTED_RING}` : "1px solid #BBBBBB",
          boxShadow: selected ? `0 0 0 2px rgba(155,90,248,.30)` : "none",
          cursor: "pointer",
          padding: 0,
          background: "transparent",
          outline: "none",
          "&:focus-visible": { boxShadow: `0 0 0 3px rgba(155,90,248,.45)` },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: inner,
            height: inner,
            borderRadius: "50%",
            overflow: "hidden",
            ...CHECKER,
            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
          }}
        >
          {/* red circle-with-slash "none" mark, centered over the checkerboard */}
          <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke={SLASH_RED}
              strokeWidth="2"
            />
            <line
              x1="5.6"
              y1="5.6"
              x2="18.4"
              y2="18.4"
              stroke={SLASH_RED}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TransparentSwatch;
